<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import relativeTime from 'dayjs/plugin/relativeTime';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	// Stores
	import ReportStore from '@/Stores/ReportStore.js';
	import ReportOutputStore from '@/Stores/ReportOutputStore.js';
	// Components
	import Icon from '@/Components/Icon.svelte';
	import Loading from '@/Components/Loading.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	// Partials
	import Preview from '@/Partials/Reports/_Preview.svelte';
	import Meta from '@/Partials/Reports/_Meta.svelte';
	import Binding from '@/Partials/Reports/_Binding.svelte';
	// Services
	import { getUrlParameter, paginationBuilder, setUrlParameter } from '@/Services/Helpers.js';
	import { notify } from '@/Services/Notify.js';
	import SelectInput from '@/Components/SelectInput.svelte';
	import { BackendReportPeriodNames, BackendReportPeriods, ReportPeriodNames, ReportPeriods } from '@/Constants/Report';

	dayjs.extend(relativeTime);
	dayjs.extend(localizedFormat);

	export let id;

	$: report = $ReportStore.firstWhere('id', id);
	$: links = $ReportOutputStore.count() && paginationBuilder(ReportOutputStore.getPagination());
	$: wantsCustomDateRange = false;
	$: selectedDatePeriod = null;
	$: report && setDatePeriod(selectedDatePeriod);

	let pageNo = getUrlParameter('page') || 1;
	let loading = loadReportOutput();
	let showPreview = false;
	let output;
	let startDate;
	let endDate = dayjs().format('YYYY-MM-DD');
	let bindingValues = {};

	async function loadReportOutput() {
		// TODO: this could be improved with ReportStore.getReport(reportId) when API supports it
		await ReportStore.getReports().catch((e) => {
			console.error(e);
			notify({
				type: 'danger',
				title: 'Error!',
				message: (e.error && e.error[0]) || 'problem occurred getting report list, try refreshing the page',
				timeout: 5
			});
		});
		return ReportOutputStore.getReportOutputs(id, { size: 50, number: pageNo }).catch((e) => {
			console.error(e);
			notify({
				type: 'danger',
				title: 'Error!',
				message: (e.error && e.error[0]) || 'problem occurred getting reports, try refreshing the page',
				timeout: 5
			});
		});
	}

	function changePage(e) {
		if (e) {
			pageNo = e.detail.page;
			loadReportOutput();
			setUrlParameter('page', pageNo);
		}
	}

	function onClick(outputId) {
		ReportOutputStore.getReportDownloadLink(outputId)
			.then((result) => {
				window.open(result.url);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem Downloading that file',
					timeout: 5
				});
			});
	}

	function onPreview(reportOutput) {
		output = reportOutput;
		showPreview = true;
	}

	function onGenerate() {
		if (report.sql_bindings && !checkBindingsValues()) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'Please enter all required fields first.',
				timeout: 3
			});
			return;
		}

		if (wantsCustomDateRange && (startDate == null || startDate == '') && (endDate == null || endDate == '')) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'Please enter either a start date, end date, or both first.',
				timeout: 3
			});
			return;
		}

		ReportOutputStore.generateReportOutput(report.id, startDate, endDate, bindingValues)
			.then(() => {
				notify({
					type: 'success',
					title: report.name,
					message: 'Report Run Scheduled. Check your email or refresh the page.',
					timeout: 3
				});
				setTimeout(() => {
					loadReportOutput();
				}, 3000);
			})
			.catch((e) => {
				console.log({ e });
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem Running the report',
					timeout: 5
				});
			});
	}

	function checkBindingsValues() {
		return Object.keys(report.sql_bindings).every((key) => bindingValues[key]);
	}

	function setDatePeriod(datePeriodSelected) {
		if (wantsCustomDateRange) {
			wantsCustomDateRange = false;
		}
		selectedDatePeriod = datePeriodSelected;
		if (datePeriodSelected == ReportPeriods.Custom) {
			wantsCustomDateRange = true;
		} else if (datePeriodSelected == ReportPeriods.Today) {
			startDate = dayjs().format('YYYY-MM-DD');
			endDate = dayjs().format('YYYY-MM-DD');
		} else if (datePeriodSelected == ReportPeriods.Yesterday) {
			startDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
			endDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
		} else if (datePeriodSelected == ReportPeriods.ThisWeek) {
			startDate = dayjs().startOf('week').add(1, 'day').format('YYYY-MM-DD');
			endDate = dayjs().endOf('week').add(1, 'day').format('YYYY-MM-DD');
		} else if (datePeriodSelected == ReportPeriods.PreviousWeek) {
			startDate = dayjs().subtract(1, 'week').startOf('week').add(1, 'day').format('YYYY-MM-DD');
			endDate = dayjs().subtract(1, 'week').endOf('week').add(1, 'day').format('YYYY-MM-DD');
		} else if (datePeriodSelected == ReportPeriods.ThisMonth) {
			startDate = dayjs().startOf('month').format('YYYY-MM-DD');
			endDate = dayjs().endOf('month').format('YYYY-MM-DD');
		} else if (datePeriodSelected == ReportPeriods.PreviousMonth) {
			startDate = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
			endDate = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
		} else if (datePeriodSelected == ReportPeriods.ThisYear) {
			startDate = dayjs().startOf('year').format('YYYY-MM-01');
			endDate = dayjs().endOf('year').format('YYYY-MM-DD');
		} else if (datePeriodSelected == ReportPeriods.PreviousYear) {
			startDate = dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD');
			endDate = dayjs().subtract(1, 'year').endOf('year').format('YYYY-MM-DD');
		} else if (datePeriodSelected == ReportPeriods.ThisFinancialYear) {
			if (dayjs().month() >= 6) {
				startDate = dayjs().format('YYYY-07-01');
				endDate = dayjs().add(1, 'year').format('YYYY-06-30');
			} else {
				startDate = dayjs().subtract(1, 'year').format('YYYY-07-01');
				endDate = dayjs().format('YYYY-06-30');
			}
		} else if (datePeriodSelected == ReportPeriods.PreviousFinancialYear) {
			if (dayjs().month() >= 6) {
				startDate = dayjs().subtract(1, 'years').format('YYYY-07-01');
				endDate = dayjs().format('YYYY-06-30');
			} else {
				startDate = dayjs().subtract(2, 'years').format('YYYY-07-01');
				endDate = dayjs().subtract(1, 'year').format('YYYY-06-30');
			}
		} else if (datePeriodSelected == ReportPeriods.Lifetime) {
			startDate = null;
			endDate = dayjs().format('YYYY-MM-DD');
		} else if ((datePeriodSelected == null || datePeriodSelected == 'null') && report.default_period) {
			if (report.default_period == BackendReportPeriods.Daily) {
				setDatePeriod(ReportPeriods.Yesterday);
			} else if (report.default_period == BackendReportPeriods.Weekly) {
				setDatePeriod(ReportPeriods.Yesterday);
			} else if (report.default_period == BackendReportPeriods.Monthly) {
				setDatePeriod(ReportPeriods.PreviousMonth);
			} else if (report.default_period == BackendReportPeriods.FinancialYear) {
				setDatePeriod(ReportPeriods.Yesterday);
			} else if (report.default_period == BackendReportPeriods.CalendarYear) {
				setDatePeriod(ReportPeriods.ThisYear);
			} else {
				startDate = null;
				endDate = dayjs().format('YYYY-MM-DD');
			}
		} else if ((datePeriodSelected == null || datePeriodSelected == 'null') && !report.default_period) {
			startDate = null;
			endDate = dayjs().format('YYYY-MM-DD');
			wantsCustomDateRange = true;
		}
	}
</script>

<Preview bind:open={showPreview} {output} />

<div>
	{#await loading}
		<Loading />
	{:then}
		{#if report}
			<div>
				<!-- Page Header -->
				<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
					<div class="flex w-full items-center">
						<a href={'/reports'} title="Report List" class="flex items-center cursor-pointer hover:underline text-grey">
							<Icon name="chevron-left" classes="w-2 mr-1" />
							<div class="mr-6 text-xl font-bold">Report List</div>
						</a>
						<h1 class="flex-1 text-xl font-bold">{report.name}</h1>
						<div class="flex items-center space-x-4">
							{#if report.sql_bindings}
								{#each Object.entries(report.sql_bindings) as binding}
									<Binding {binding} bind:value={bindingValues[binding[0]]} />
								{/each}
							{/if}
							{#if report.is_custom_periodic}
								<div class="flex flex-row items-center">
									<span class="font-semibold">Date period:</span>
									<SelectInput
										className="ml-2"
										name="sport"
										required={true}
										on:change={(e) => setDatePeriod(e.target.value)}
									>
										{#if report.default_period}
											<option value={null} selected>Default: {BackendReportPeriodNames[report.default_period]}</option>
										{:else}
											<option value={null}>Select Date Period...</option>
										{/if}
										{#each Object.entries(ReportPeriodNames) as ReportPeriod}
											<option value={ReportPeriod[0]}>{ReportPeriod[1]}</option>
										{/each}
									</SelectInput>
								</div>
								<div>
									<span class="font-semibold">Start Date:</span>
									<input
										type="date"
										bind:value={startDate}
										disabled={!wantsCustomDateRange}
										class="border-b border-row-separator focus:outline-none"
									/>
								</div>
								<div>
									<span class="font-semibold">End Date:</span>
									<input
										type="date"
										bind:value={endDate}
										disabled={!wantsCustomDateRange}
										class="border-b border-row-separator focus:outline-none"
									/>
								</div>
							{/if}
							<button class="btn btn-black" on:click={onGenerate}> Run Report </button>
						</div>
					</div>
				</div>
				<!-- /Page Header -->
				<div class="p-2 mx-4 bg-gray-100 rounded">
					<!-- <Pagination {links} on:click={changePage} /> -->
					<div class="mt-2 rounded">
						<h2 class="text-center uppercase">Available Downloads</h2>
						<table class="w-full whitespace-nowrap">
							<thead>
								<tr class="text-xs text-left text-gray-500 uppercase">
									<th class="px-6 pt-5 pb-4 font-medium">File</th>
									<th class="px-6 pt-5 pb-4 font-medium">Options</th>
									<th class="px-6 pt-5 pb-4 font-medium">Date</th>
									<th class="px-6 pt-5 pb-4 font-medium" />
									<th class="px-6 pt-5 pb-4 font-medium" />
								</tr>
							</thead>
							<tbody class="bg-white">
								{#each $ReportOutputStore.sortByDesc('created_at').all() as output (output.id)}
									<tr
										on:click={() => onClick(output.id)}
										class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100"
									>
										<td class="p-4 px-6 font-semibold border-t">
											{output.name}
										</td>
										<td class="p-4 px-6 border-t">
											{#if output.meta}
												<Meta data={output.meta} />
											{/if}
										</td>
										<td class="p-4 px-6 border-t">
											{dayjs(output.created_at).format('lll')}
										</td>
										<td class="w-px px-6 border-t">
											<Icon name="download-cloud" classes="block inline" />
										</td>
										<td class="w-px px-6 border-t">
											<button
												on:click|stopPropagation={() => onPreview(output)}
												class="hover:text-gray-600 focus:outline-none"
												title="View in Browser"
											>
												<Icon name="show" classes="block inline" />
											</button>
										</td>
									</tr>
								{:else}
									<tr>
										<td class="px-6 py-4 text-center border-t" colspan="5"> No downloads found. </td>
									</tr>
								{/each}
							</tbody>
						</table>
					</div>
					<Pagination {links} on:click={changePage} className="mt-2" />
				</div>
			</div>
		{/if}
	{/await}
</div>
